import React, { useContext, useReducer, useMemo, useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { FormField2 as Field } from 'components';
import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table";
import { useTheme } from "@table-library/react-table-library/theme";
import { getTheme } from "@table-library/react-table-library/baseline";
import { Virtualized } from "@table-library/react-table-library/virtualized";
import { useTree, CellTree, TreeExpandClickTypes } from "@table-library/react-table-library/tree";
import { 
  IconButton, 
  ButtonGroup, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  Typography,
  Menu,
  MenuItem, 
} from '@material-ui/core';
import {
  MoreVert as MoreVertIcon,
} from '@material-ui/icons';
import ActionMenu from './WorkReportActionMenu';
import TreeEditableCell from './TreeEditableCell';

const ROW_HEIGHT = 36;
const ROW_HEIGHT_WITH_COEFS = 40;
const ROW_HEIGHT_EXPANDED = 64;

import {
  WorkReportContext,
  TYPES,
  DETAIL_LEVEL,
} from './WorkReportForm';

const WorkReportTable = () => {
  const { t } = useTranslation();
  const {
    record,
    treeExpandedRowKeys,
    setTreeExpandedRowKeys,
    itemActions,
    setShowObjectSummaryModal,
  } = useContext(WorkReportContext);

  const data = { nodes: record?.items };

  const onTreeChange = (action, state) => {
    setTreeExpandedRowKeys(state.ids);
  };
  
  const tree = useTree(data, {
    onChange: onTreeChange,
    state: { ids: treeExpandedRowKeys || [] },
  }, {
    // treeYLevel: 1,
    indentation: 5,
    clickType: TreeExpandClickTypes?.ButtonClick,
    treeIcon: {
      size: '10px',
      // margin: '4px',
      iconRight: <>&#x25BA;</>,
      iconDown: <>&#x25BC;</>,
    },
  });

  const theme = useTheme({
    Table: `
        --data-table-library_grid-template-columns:  10% 17% 5% 5% 7% 7% 7% 7% 7% 10% 10% 5% 3%;
      `,
    BaseCell: `
      &:nth-of-type(1) {
        left: 0px;
      }

      &:nth-of-type(2) {
        left: 110px;
      }
    `,
  });

  const renderType = (text, record) => {
    let value = typeLangMap[text] || text;

    if (text === TYPES.RATE && record.rateType) {
      value = `${t('Įk.')} ${rateTypeLangMap[record.rateType].toLowerCase()}`;
    } else if (text === TYPES.RESOURCE && record.resourceType) {
      value = resourceTypeLangMap[record.resourceType];
    }

    return (
      <span title={value}>{value}</span>
    )
  }

  const typeLangMap = {
    'estimate': t('Sąmata'),
    'part': t('Sąm. dalis'),
    'chapter': t('Skyrius'),
    'subchapter': t('Poskyris'),
    'group': t('El. grupė'),
    'element': t('Elementas'),
    'rate': t('Įkainis'),
    'resource': t('Resursas'),
  };

  const resourceTypeLangMap = {
    'work': t('Darbas'),
    'mechanism': t('Mechanizmas'),
    'material': t('Medžiaga'),
    'equipment': t('Įrenginys'),
    'other': t('Kita'),
  };

  const rateTypeLangMap = {
    'work': t('Darbas'),
    'mechanism': t('Mechanizmas'),
    'material': t('Medžiaga'),
    'equipment': t('Įrenginys'),
  };

  const handleCellChange = ({ name, value, item }) => {
    itemActions.cellChanged(name, item, value);
  }

  const handleValueRound = (value) => {
    if (value === null || value === undefined) return value;

    if ([0, 1, 2, 3].includes(record?.totals?.numbersAfterDecimal)) {
      return parseFloat(value).toFixed(Math.abs(record.totals.numbersAfterDecimal));
    } else {
      return parseFloat(value).toFixed(2);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{ margin: '16px 0px 8px 0px', display: 'flex' }}
        >
          <Field
            label={t('Detalizavimas')}
            name="detail-level"
            // value={treeDetailLevel}
            value={null}
            placeholder={t('Pasirinkite...')}
            type="select"
            options={[
              { value: DETAIL_LEVEL.ESTIMATE, label: t('Sąmata') },
              { value: DETAIL_LEVEL.PART, label: t('Sąmatos dalis') },
              { value: DETAIL_LEVEL.CHAPTER, label: t('Skyrius') },
              { value: DETAIL_LEVEL.SUBCHAPTER, label: t('Poskyris') },
              { value: DETAIL_LEVEL.GROUP, label: t('El. grupė') },
              { value: DETAIL_LEVEL.ELEMENT, label: t('Elementas') },
              { value: DETAIL_LEVEL.RATE, label: t('Įkainis') },
              { value: DETAIL_LEVEL.RESOURCE, label: t('Įkainis ir resursai') },
            ]}
            style={{ width: 140 }}
            onChange={({ value }) => {
              if (value === DETAIL_LEVEL.ESTIMATE) {
                tree.fns.onRemoveAll();
                setTreeExpandedRowKeys([]);
                return;
              }

              let ids = [];

              const loopNodes = (nodes) => {
                nodes.forEach(node => {
                  if (node.type == value) {
                    ids = ids.concat(node.path.filter(v => v !== node._id));
                  }
                  if (node?.nodes?.length) loopNodes(node.nodes);
                });
              }

              loopNodes(data.nodes);
              tree.fns.onRemoveAll();
              tree.fns.onAddByIds(ids);
              setTreeExpandedRowKeys(ids);
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 130 }}>
            <Typography style={{ fontWeight: 600 }}>{t('Periodas')}</Typography>
            <Typography>{record?.period || ''}</Typography>
          </div>
          <div style={{ width: 130 }}>
            <Typography style={{ fontWeight: 600 }}>{t('Rangovas')}</Typography>
            <Typography>{record?.contractor || ''}</Typography>
          </div>
          <div style={{ width: 130 }}>
            <Typography style={{ fontWeight: 600 }}>{t('Darbų vadovas')}</Typography>
            <Typography>{record?.manager || ''}</Typography>
          </div>
          <div style={{ width: 130 }}>
            <Typography style={{ fontWeight: 600 }}>{t('Kainyno versija')}</Typography>
            <Typography>{record?.version?.version || ''}</Typography>
          </div>
          <div style={{ width: 130 }}>
            <Typography style={{ fontWeight: 600 }}>{t('Suma, EUR be PVM')}</Typography>
            <Typography>{handleValueRound(record?.totals?.amountReported)}</Typography>
          </div>
          <div style={{ width: 130, paddingLeft: 10 }}>
            <Typography style={{ fontWeight: 600 }}>{t('Suma, EUR su PVM')}</Typography>
            <Typography>{handleValueRound(record?.totals?.grandTotal)}</Typography>
          </div>
          <div style={{ paddingLeft: 10 }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ fontSize: '10px', marginTop: '5px' }}
              onClick={() => setShowObjectSummaryModal(true)}>
              {t('Kainos sudėtis')}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ height: `${window.innerHeight - 400}px` }} >
        <Table
          data={data}
          layout={{
            isDiv: true,
            fixedHeader: true,
            custom: true,
          }}
          tree={tree}
          className="tree-table"
          theme={theme}
        >
          {(tableList) => (
            <Virtualized
              tableList={tableList}
              rowHeight={ROW_HEIGHT}
              header={() => (
                <HeaderRow>
                  <HeaderCell stiff pinLeft resize>{t('Kodas')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Pavadinimas')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Mato vnt.')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Norma')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Kiekis')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Kaina')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Suma')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Kiekis (likutis)')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Suma (likutis)')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Kiekis (aktuojamas)')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Suma (aktuojama)')}</HeaderCell>
                  <HeaderCell pinLeft resize>{t('Tipas')}</HeaderCell>
                  <HeaderCell pinLeft resize />
                </HeaderRow>
              )}
              body={(item, index) => (
                <Row
                  id={item._id}
                  item={item}
                  style={item.type === "resource" && (item.norm == 0 || item.price == 0 || item.amount == 0) ? { color: "red" } : null}
                  className={`tree-row-type-${item.type} row-key-${item._id}`}
                >
                  {!!item.nodes?.length ? (
                    <CellTree
                      item={item}
                      pinLeft
                      style={{ paddingLeft: `${item.level * 5}px` }}
                    >
                      <TreeEditableCell
                        readOnly
                        name="code"
                        value={item.code || '-'}
                        item={item}
                        inputProps={{ type: 'text' }}
                      />
                    </CellTree>
                  ) : (
                    <Cell
                      pinLeft={true}
                      style={{ textAlign: 'left', paddingLeft: `${item.level * 5 + 5}px` }}
                    >
                      <TreeEditableCell
                        readOnly
                        name="code"
                        value={item.code || '-'}
                        item={item}
                      />
                    </Cell>
                  )}
                  <Cell pinLeft style={{ textAlign: 'left' }}>
                    <TreeEditableCell
                      readOnly
                      name="title"
                      value={item.title}
                      item={item}
                      type="textarea"
                      inputProps={{ rows: 5 }}
                      style={
                        item.type === 'rate' ? { paddingRight: '20px' } : {}
                      }
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'center' }}>
                    <TreeEditableCell
                      readOnly
                      name="unit"
                      value={item.unit}
                      item={item}
                      inputProps={{ type: 'text' }}
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right' }}>
                    <TreeEditableCell
                      readOnly
                      name="norm"
                      value={item.norm}
                      item={item}
                      style={item.type === 'resource' && item?.overrides?.norm ?
                        { textDecorationLine: 'underline', textDecorationStyle: 'dashed', textDecorationColor: '#03A9F4' }
                        : {}
                      }
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right', position: 'relative' }}>
                    <TreeEditableCell
                      readOnly
                      name="quantity"
                      value={item.quantity}
                      item={item}
                      style={{ paddingLeft: '20px' }}
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right' }}>
                    <TreeEditableCell
                      readOnly
                      name="price"
                      value={handleValueRound(item.price)}
                      onChange={handleCellChange}
                      item={item}
                      style={item.type === 'resource' && _.get(item, `overrides.prices[${record.version.version}]`) ?
                        { textDecorationLine: 'underline', textDecorationStyle: 'dashed', textDecorationColor: '#03A9F4' }
                        : {}
                      }
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right' }}>
                    <TreeEditableCell
                      readOnly
                      name="amount"
                      value={handleValueRound(item.amount)}
                      onChange={handleCellChange}
                      item={item}
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right' }}>
                    <TreeEditableCell
                      readOnly
                      name="quantityBefore"
                      value={['resource', 'rate'].includes(item.type) ? handleValueRound(item.quantityBefore) : null}
                      onChange={handleCellChange}
                      item={item}
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right' }}>
                    <TreeEditableCell
                      readOnly
                      name="amountBefore"
                      value={handleValueRound(item.amountBefore)}
                      onChange={handleCellChange}
                      item={item}
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right' }}>
                    <TreeEditableCell
                      readOnly={item.type !== 'rate'}
                      name="quantityReported"
                      value={['resource', 'rate'].includes(item.type) ? handleValueRound(item.quantityReported) : null}
                      onChange={handleCellChange}
                      item={item}
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'right' }}>
                    <TreeEditableCell
                      readOnly
                      name="amountReported"
                      value={handleValueRound(item.amountReported)}
                      onChange={handleCellChange}
                      item={item}
                    />
                  </Cell>
                  <Cell style={{ textAlign: 'center' }}>{renderType(item.type, item)}</Cell>
                  <Cell style={{ textAlign: 'center', padding: 0 }} pinRight>
                    {item.type !== 'resource' && 
                      <ActionMenu item={item} />
                    }
                  </Cell>
                </Row>
              )}
            />
          )}
        </Table>
      </div >
    </>
  )
}

export default WorkReportTable;